import { Button, Drawer, Input } from "antd";
import {
  CloseOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  LinkOutlined,
} from "@ant-design/icons";

import { useEffect, useRef, useState } from "react";
import axios from "axios";
import LoadingSpinner from "./loaders/LoadingSpinner";
import SuccessPopup from "./modals/SuccessModal";
import ErrorPopup from "./modals/ErrorModal";
import { useGetInActiveModules } from "../../../hooks/useGetInactiveModules";
import { useGetActiveModules } from "../../../hooks/useGetActiveModules";

export default function ModuleEditModal({ open, onClose, values, index }) {
  const [name, setName] = useState("");
  const [shortName, setShortName] = useState("");
  const [description, setDescription] = useState("");
  const [tempUpload, setTempUpload] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const fileInputRef = useRef(null);

    const {
      data: activeModules,
      isLoading: activeModulesLoading,
      refetch: activeModulesRefetch,
    } = useGetActiveModules();
    const {
      data: inActiveModules,
      isLoading: inActiveModulesLoading,
      refetch: inactiveModulesRefetch,
    } = useGetInActiveModules();

  // Sync values into local state when drawer opens
  useEffect(() => {
    if (values && open) {
      setName(values.name || "");
      setShortName(values.short_name || "");
      setDescription(values.description || "");
      setTempUpload(values.image || "");
    }
  }, [values, open]);

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const [uploadedFile, setUploadedFile] = useState(null); // Track actual File object

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type)) {
      console.error("Invalid image file (JPEG, JPG, or PNG only)");
      return;
    }

    const imageUrl = URL.createObjectURL(file);
    setTempUpload(imageUrl);
    setUploadedFile(file); // Set new file
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const token = localStorage.getItem("superAdminToken");
      if (!token) {
        console.error("Token not found");
        return;
      }

      console.log({ name, shortName, status: values?.status });

      const formData = new FormData();
      formData.append("name", name);
      formData.append("short_name", shortName);
      formData.append("description", description);
      formData.append("status", values?.status);

      if (uploadedFile) {
        formData.append("image", uploadedFile);
      }


      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/super-admin/update-module/${values?.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setSuccessMessage(response.data.message);
      inactiveModulesRefetch();
      activeModulesRefetch();
      console.log({ response });
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
      onClose(index); // Close drawer after success
    } catch (error) {
      setIsLoading(false);
      console.error("Update failed:", error);
      const apiErrors = error?.response?.data?.errors;
      const fallbackMessage =
        error?.response?.data?.message || "Something went wrong";

      if (apiErrors && typeof apiErrors === "object") {
        // Always extract the first message
        const firstErrorMessage =
          Object.values(apiErrors)[0]?.[0] || fallbackMessage;
        setErrorMessage(firstErrorMessage);
      } else {
        setErrorMessage(fallbackMessage);
      }

      console.error("Error:", error.response?.data || error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {successMessage && (
        <SuccessPopup
          message={successMessage}
          onClose={() => setSuccessMessage(null)}
        />
      )}
      {errorMessage && (
        <ErrorPopup
          message={errorMessage}
          onClose={() => setErrorMessage(null)}
        />
      )}

      <Drawer
        className="add_module_edit_modal rounded-3 z-3"
        open={open}
        onClose={() => onClose(index)}
        closable={false}
        width={window.innerWidth < 768 ? "100%" : 600}
        height={window.innerWidth < 768 ? "100%" : "auto"}
        placement="right"
      >
        <div className="modal-header">
          <div className="header-content">
            <h1 className="box-header">Edit Module</h1>
            <p>Proceed to edit this module</p>
          </div>
          <CloseOutlined
            className="close-icon"
            onClick={() => onClose(index)}
          />
        </div>

        <form className="input-container" onSubmit={handleModalSubmit}>
          <div className="input-group1">
            <div className="input-field">
              <label htmlFor="module_name">Name:</label>
              <Input
                placeholder="Billings Management"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="input-field">
              <label htmlFor="module_price">Short Name:</label>
              <Input
                value={shortName}
                onChange={(e) => setShortName(e.target.value)}
              />
            </div>
          </div>

          <div className="description-field">
            <label>Description</label>
            <div className="rich-text-toolbar">
              <select className="font-select">
                <option>Sans Serif</option>
              </select>
              <div className="formatting-buttons">
                <button type="button">
                  <BoldOutlined />
                </button>
                <button type="button">
                  <ItalicOutlined />
                </button>
                <button type="button">
                  <UnderlineOutlined />
                </button>
                <button type="button">
                  <LinkOutlined />
                </button>
                <button type="button">
                  <OrderedListOutlined />
                </button>
                <button type="button">
                  <UnorderedListOutlined />
                </button>
              </div>
            </div>
            <Input.TextArea
              id="text-area"
              placeholder="Enter module description"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="upload-section">
            <h3>Upload Icon</h3>
            <p>
              Select and upload the preferred icon of your module from your
              media
            </p>
            <div
              className="upload-area position-relative"
              onClick={handleFileButtonClick}
            >
              <img
                src={tempUpload}
                alt="uploaded"
                width={120}
                height={120}
                className="uploaded-image position-absolute"
              />
              <img
                src="/assets/superAdmin/Upload.svg"
                alt="upload"
                width={50}
                height={50}
                className="upload-icon"
              />
              <p>Change File</p>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".jpg,.jpeg,.png"
                style={{ display: "none" }}
              />
            </div>
          </div>

          <div className="w-100 d-flex align-items-center justify-content-center">
            <Button className="add-client-button" htmlType="submit">
              {isLoading ? <LoadingSpinner /> : "Update"}
            </Button>
          </div>
        </form>
      </Drawer>
    </>
  );
}
